import React, { useEffect, useRef, useState } from 'react';
import { Shield, Target, Award, TrendingUp, CheckCircle, XCircle } from 'lucide-react';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600;700&display=swap');

  .dbe-w *, .dbe-w *::before, .dbe-w *::after {
    font-family: 'Outfit', sans-serif;
    box-sizing: border-box;
  }
  .dbe-w h1, .dbe-w h2, .dbe-w h3, .dbe-w h4, .dbe-w h5, .dbe-w h6 {
    font-family: 'Cormorant Garamond', serif;
  }

  .dbe-w {
    --navy:       #0A1535;
    --navy-mid:   #0D1D45;
    --blue:       #1B4FD8;
    --blue-mid:   #2563EB;
    --blue-bright:#3B7BFF;
    --gold:       #D4A017;
    --gold-light: #F5D080;
    --green:      #059669;
    --green-bg:   #ECFDF5;
    --green-border:#A7F3D0;
    --red:        #DC2626;
    --red-bg:     #FEF2F2;
    --red-border: #FECACA;
    --ice:        #F0F5FF;
    --ice-mid:    #DBEAFE;
    --border:     #E2E8F0;
    --text:       #1E293B;
    --muted:      #64748B;
    --dim:        #94A3B8;
    --white:      #FFFFFF;
    font-family: 'Outfit', sans-serif;
    background: var(--white);
  }

  .dbe-w-section {
    background: var(--white);
    position: relative; overflow: hidden;
    padding: 112px 0;
  }

  .dbe-w-grid {
    position: absolute; inset: 0; pointer-events: none; z-index: 0;
    background-image:
      linear-gradient(rgba(59,123,255,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(59,123,255,0.04) 1px, transparent 1px);
    background-size: 52px 52px;
  }

  .dbe-w-wash {
    position: absolute; top: -200px; right: -200px;
    width: 700px; height: 700px; border-radius: 50%;
    background: radial-gradient(circle, rgba(59,123,255,0.06) 0%, transparent 70%);
    pointer-events: none; z-index: 0;
  }

  .dbe-w-wash2 {
    position: absolute; bottom: -150px; left: -150px;
    width: 500px; height: 500px; border-radius: 50%;
    background: radial-gradient(circle, rgba(212,160,23,0.05) 0%, transparent 70%);
    pointer-events: none; z-index: 0;
  }

  .dbe-w-topline {
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, transparent, var(--blue-mid) 30%, var(--gold) 70%, transparent);
  }

  .dbe-w-inner {
    max-width: 1280px; margin: 0 auto;
    padding: 0 64px; position: relative; z-index: 2;
  }

  .dbe-w-layout {
    display: flex; flex-direction: column; gap: 40px;
  }

  .dbe-w-content-row {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 72px; align-items: stretch;
  }

  .dbe-w-chapter {
    display: inline-flex; align-items: center; gap: 12px; margin-bottom: 28px;
  }
  .dbe-w-dash { width: 36px; height: 2px; background: var(--gold); }
  .dbe-w-label {
    font-family: 'Outfit', sans-serif;
    font-size: 10px; font-weight: 600; letter-spacing: 0.30em; text-transform: uppercase;
    color: var(--gold); display: flex; align-items: center; gap: 8px;
  }

  .dbe-w-h1 {
    font-family: 'Cormorant Garamond', serif;
    font-size: 40px;
    font-weight: 900; line-height: 1.04;
    color: #3B7BFF; margin: 0 0 6px;
    letter-spacing: -0.02em;
  }
  .dbe-w-h1 em { font-style: italic; font-weight: 600; color: var(--blue-bright); }

  .dbe-w-sub {
  font-family: 'Outfit', sans-serif;
  font-size: 20px; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase;
  color: var(--dim); margin-bottom: 36px;
  display: flex; align-items: center; gap: 10px;
  justify-content: center;
}
.dbe-w-sub::before,
.dbe-w-sub::after { content: ''; flex: 0 0 20px; height: 1px; background: var(--border); }

  .dbe-w-p { font-family: 'Outfit', sans-serif; font-size: 16px; line-height: 1.82; color: var(--muted); margin-bottom: 16px; max-width: 580px; }
  .dbe-w-p strong { color: var(--navy); font-weight: 600; }

  .dbe-w-callout {
    margin: 24px 0 0 0;
    background: var(--navy);
    padding: 30px 32px;
    border-left: 4px solid var(--gold);
    position: relative; overflow: hidden;
    box-shadow: 0 16px 48px rgba(10,21,53,0.12);
  }
  .dbe-w-callout::before {
    content: '"'; position: absolute; right: 16px; bottom: -24px;
    font-family: 'Cormorant Garamond', serif; font-size: 110px; font-weight: 600;
    color: rgba(255,255,255,0.05); line-height: 1; pointer-events: none;
  }
  .dbe-w-callout-head {
    display: flex; align-items: center; gap: 14px; margin-bottom: 12px;
  }
  .dbe-w-callout-icon {
    width: 38px; height: 38px; flex-shrink: 0;
    background: rgba(212,160,23,0.15); border: 1px solid rgba(212,160,23,0.3);
    display: flex; align-items: center; justify-content: center;
  }
  .dbe-w-callout-title { font-family: 'Outfit', sans-serif; font-size: 15px; font-weight: 600; color: white; }
  .dbe-w-callout-text { font-family: 'Outfit', sans-serif; font-size: 14px; line-height: 1.76; color: rgba(255,255,255,0.72); }

  /* ── NEW COMPARISON CARDS ── */
  .dbe-w-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-top: 36px; align-items: stretch; }

  .dbe-w-card-creation {
    background: var(--green-bg);
    border: 1.5px solid var(--green-border);
    border-top: 4px solid var(--green);
    padding: 28px 24px;
    position: relative; overflow: hidden;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    display: flex; flex-direction: column;
  }
  .dbe-w-card-creation::after {
    content: '';
    position: absolute; top: -30px; right: -30px;
    width: 90px; height: 90px; border-radius: 50%;
    background: radial-gradient(circle, rgba(5,150,105,0.1) 0%, transparent 70%);
  }
  .dbe-w-card-creation:hover { transform: translateY(-5px); box-shadow: 0 20px 50px rgba(5,150,105,0.15); }

  .dbe-w-card-extraction {
    background: var(--red-bg);
    border: 1.5px solid var(--red-border);
    border-top: 4px solid var(--red);
    padding: 28px 24px;
    position: relative; overflow: hidden;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    display: flex; flex-direction: column;
  }
  .dbe-w-card-extraction::after {
    content: '';
    position: absolute; top: -30px; right: -30px;
    width: 90px; height: 90px; border-radius: 50%;
    background: radial-gradient(circle, rgba(220,38,38,0.08) 0%, transparent 70%);
  }
  .dbe-w-card-extraction:hover { transform: translateY(-5px); box-shadow: 0 20px 50px rgba(220,38,38,0.12); }

  .dbe-w-card-badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 12px 4px 8px;
    border-radius: 100px;
    font-size: 10px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
    margin-bottom: 16px;
  }
  .dbe-w-card-badge-green {
    background: rgba(5,150,105,0.12);
    border: 1px solid rgba(5,150,105,0.25);
    color: var(--green);
  }
  .dbe-w-card-badge-red {
    background: rgba(220,38,38,0.08);
    border: 1px solid rgba(220,38,38,0.2);
    color: var(--red);
  }

  .dbe-w-card-heading {
    font-family: 'Cormorant Garamond', serif;
    font-size: 22px; font-weight: 600; line-height: 1.2;
    margin-bottom: 14px;
  }
  .dbe-w-card-heading-green { color: #065F46; }
  .dbe-w-card-heading-red { color: #991B1B; }

  .dbe-w-card-body {
    font-size: 13px; line-height: 1.78; color: var(--muted);
    margin-bottom: 0; flex: 1;
  }
  .dbe-w-card-body-green strong { color: #065F46; font-weight: 600; }
  .dbe-w-card-body-red strong { color: #991B1B; font-weight: 600; }

  .dbe-w-card-divider {
    height: 1px; margin: 16px 0;
    background: linear-gradient(90deg, rgba(5,150,105,0.2) 0%, transparent 100%);
    flex-shrink: 0;
  }
  .dbe-w-card-divider-red {
    background: linear-gradient(90deg, rgba(220,38,38,0.15) 0%, transparent 100%);
  }

  .dbe-w-card-footer {
    display: flex; align-items: center; gap: 8px;
    font-size: 11px; font-weight: 600; letter-spacing: 0.04em;
    flex-shrink: 0;
  }
  .dbe-w-card-footer-green { color: var(--green); }
  .dbe-w-card-footer-red { color: var(--red); }

  /* ── QUOTE BAR ── */
  .dbe-w-qbar {
    margin-top: 24px; padding: 18px 24px;
    background: var(--ice); border: 1px solid var(--ice-mid);
    border-left: 4px solid var(--blue-mid);
    display: flex; gap: 14px; align-items: flex-start;
  }
  .dbe-w-qbar-text { font-family: 'Outfit', sans-serif; font-size: 14px; line-height: 1.74; color: var(--muted); font-style: italic; }
  .dbe-w-qbar-text strong { color: var(--navy); font-style: normal; font-weight: 600; }

  /* ── VIDEO ── */
  .dbe-w-video-block {
    position: relative;
    border: 1px solid var(--border);
    box-shadow: 0 32px 80px rgba(10,21,53,0.12);
    overflow: hidden;
  }

  .dbe-w-video-ratio {
    position: relative; width: 100%; aspect-ratio: 16/9;
  }

  .dbe-w-video-box { position: absolute; inset: 0; }
  .dbe-w-video-box video { width: 100%; height: 100%; object-fit: cover; display: block; }
  .dbe-w-video-grad {
    position: absolute; inset: 0;
    background: linear-gradient(to bottom, rgba(10,21,53,0.05) 0%, transparent 25%, transparent 65%, rgba(10,21,53,0.65) 100%);
  }

  .dbe-w-bk-tl {
    position: absolute; top: -10px; left: -10px; z-index: 2;
    width: 40px; height: 40px;
    border-top: 2px solid var(--gold); border-left: 2px solid var(--gold);
  }
  .dbe-w-bk-br {
    position: absolute; bottom: -10px; right: -10px; z-index: 2;
    width: 40px; height: 40px;
    border-bottom: 2px solid var(--blue-mid); border-right: 2px solid var(--blue-mid);
  }

  .dbe-w-chip {
    position: absolute; bottom: 24px; left: 24px; z-index: 3;
    background: rgba(10,21,53,0.82); backdrop-filter: blur(18px);
    border: 1px solid rgba(59,123,255,0.25);
    padding: 14px 20px; display: flex; align-items: center; gap: 14px;
  }
  .dbe-w-chip-dot {
    width: 8px; height: 8px; border-radius: 50%; background: var(--gold); flex-shrink: 0;
    box-shadow: 0 0 0 3px rgba(212,160,23,0.2);
    animation: dbe-w-blink 2s ease-in-out infinite;
  }
  @keyframes dbe-w-blink { 0%,100%{opacity:1} 50%{opacity:0.3} }
  .dbe-w-chip-main { font-family: 'Outfit', sans-serif; font-size: 12px; font-weight: 600; color: white; letter-spacing: 0.06em; text-transform: uppercase; }
  .dbe-w-chip-sub { font-family: 'Outfit', sans-serif; font-size: 10px; color: rgba(255,255,255,0.55); margin-top: 2px; }

  /* ── ANIMATIONS ── */
  .dbe-w-fade { opacity: 0; transform: translateY(28px); transition: opacity 0.8s ease, transform 0.8s ease; }
  .dbe-w-fade.on { opacity: 1; transform: none; }
  .dbe-w-slide-r { opacity: 0; transform: translateX(36px); transition: opacity 0.9s ease, transform 0.9s ease; }
  .dbe-w-slide-r.on { opacity: 1; transform: none; }

  @media (max-width: 1100px) {
    .dbe-w-content-row { grid-template-columns: 1fr; gap: 48px; }
    .dbe-w-inner { padding: 0 28px; }
  }
  @media (max-width: 640px) {
    .dbe-w-section { padding: 80px 0; }
    .dbe-w-cards { grid-template-columns: 1fr; }
    
  }
`;

const DontBeEvilSection = () => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <section className="dbe-w dbe-w-section" ref={ref}>
        <div className="dbe-w-grid" />
        <div className="dbe-w-wash" />
        <div className="dbe-w-wash2" />
        <div className="dbe-w-topline" />

        <div className="dbe-w-inner">
          <div className="dbe-w-layout">

            <div className="dbe-w-content-row">

              {/* LEFT col — heading + text + callout */}
              <div className={`dbe-w-fade ${vis ? 'on' : ''}`} style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="dbe-w-chapter">
                  <div className="dbe-w-dash" />
                  <span className="dbe-w-label"><Shield size={11} color="#D4A017" /> Ethical Business First</span>
                </div>
                <h2 className="dbe-w-h1"> <em>Integrity · Transparency · Ethics</em></h2><br />
                <p className="dbe-w-sub">Don't Be Evil</p>
                <p className="dbe-w-p">Vibrant Viva is guided by a simple principle — business should always create value and never harm people. We promote openness, fairness, and meaningful intentions in everything we do.</p>
                <p className="dbe-w-p">In today's business environment, distinguishing between genuine opportunities and unethical enterprises has become increasingly challenging. At Vibrant Viva, we believe that responsibility, regulatory compliance, and transparency are fundamental to building trust.</p>

                {/* Callout moved to left col */}
                <div className="dbe-w-callout" style={{ marginTop: 'auto' }}>
                  <div className="dbe-w-callout-head">
                    <div className="dbe-w-callout-icon"><Target size={18} color="#D4A017" /></div>
                    <span className="dbe-w-callout-title">Good Intentions Aren't Enough</span>
                  </div>
                  <p className="dbe-w-callout-text">A person may wish to save a life, but without proper driving skills, operating an ambulance can put lives at risk. Similarly, in business, good intentions must be supported by competence, ethics, and responsibility.</p>
                </div>
              </div>

              {/* RIGHT col — comparison cards only */}
              <div className={`dbe-w-fade ${vis ? 'on' : ''}`} style={{ transitionDelay: '150ms', display: 'flex', flexDirection: 'column', gap: '14px' }}>

                {/* Value Creation */}
                <div className="dbe-w-card-creation" style={{ flex: 1 }}>
                  <div className="dbe-w-card-badge dbe-w-card-badge-green">
                    <CheckCircle size={10} />
                    Value Creation
                  </div>
                  <div className="dbe-w-card-heading dbe-w-card-heading-green">Building Genuine Benefit</div>
                  <div className="dbe-w-card-divider" />
                  <p className="dbe-w-card-body dbe-w-card-body-green">
                    Creating genuine benefit by offering <strong>quality products</strong>, building skills, and supporting long-term growth for individuals and customers. Rooted in <strong>integrity, transparency, and ethical practices</strong> — ensuring every effort contributes positively to people's lives. This approach reflects the true spirit of "Don't Be Evil," where success is achieved by <strong>uplifting others</strong>.
                  </p>
                  <div className="dbe-w-card-divider" />
                  <div className="dbe-w-card-footer dbe-w-card-footer-green">
                    <CheckCircle size={13} />
                    The true spirit of "Don't Be Evil"
                  </div>
                </div>

                {/* Value Extraction */}
                <div className="dbe-w-card-extraction" style={{ flex: 1 }}>
                  <div className="dbe-w-card-badge dbe-w-card-badge-red">
                    <XCircle size={10} />
                    Value Extraction
                  </div>
                  <div className="dbe-w-card-heading dbe-w-card-heading-red">Short-Term Taking</div>
                  <div className="dbe-w-card-divider dbe-w-card-divider-red" />
                  <p className="dbe-w-card-body dbe-w-card-body-red">
                    Focusing on short-term gains by <strong>taking advantage of people</strong> through pressure, forced purchases, or unfair practices without delivering real value. Quick results that erode trust and <strong>destroy sustainability</strong>. This stands in direct contradiction to the "Don't Be Evil" principle, as it <strong>prioritizes profit over people</strong>.
                  </p>
                  <div className="dbe-w-card-divider dbe-w-card-divider-red" />
                  <div className="dbe-w-card-footer dbe-w-card-footer-red">
                    <XCircle size={13} />
                    Contradiction of "Don't Be Evil"
                  </div>
                </div>

              </div>
            </div>

            {/* QBAR — full width below the two columns */}
            <div className={`dbe-w-fade ${vis ? 'on' : ''}`} style={{ transitionDelay: '200ms' }}>
              <div className="dbe-w-qbar">
                <p className="dbe-w-qbar-text">True entrepreneurship thrives in organizations that respect the <strong>hard work of direct sellers</strong> and remain committed to delivering authentic, uncompromised products.</p>
              </div>
            </div>

            {/* VIDEO */}
            <div className={`dbe-w-fade ${vis ? 'on' : ''}`} style={{ transitionDelay: '300ms' }}>
              <div className="dbe-w-video-block">
                <div className="dbe-w-bk-tl" />
                <div className="dbe-w-bk-br" />
                <div className="dbe-w-video-ratio">
                  <div className="dbe-w-video-box">
                    <video src="/assets/leadership.mov" autoPlay loop muted playsInline />
                    <div className="dbe-w-video-grad" />
                  </div>
                  <div className="dbe-w-chip">
                    <div className="dbe-w-chip-dot" />
                    <div>
                      <div className="dbe-w-chip-main">12 Years of Ethical Business</div>
                      <div className="dbe-w-chip-sub">Full Legal Compliance · Verified & Transparent</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
};

export default DontBeEvilSection;